import {
    addHours,
    addDays,
    addWeeks,
    addMonths,
    addMinutes,
    differenceInHours,
    differenceInDays,
    differenceInWeeks,
    differenceInCalendarMonths,
} from "date-fns";
import { STALLED_GRACE_MINUTES } from "constants/schedule-monitor.constants";
import { ScheduleStatus } from "enums/schedule.enum";
import { TaskStatus } from "enums/task.enum";
import { Repeats } from "types/reports/enums";
import { IScheduleRowViewModel } from "types/schedule";
import { ITaskRowViewModel } from "types/task";

export enum DerivedScheduleStatus {
    FAILED = "failed",
    STALLED = "stalled",
    UNKNOWN = "unknown",
}

/**
 * The most recent tick a recurring schedule was due to fire at or before `now`, or null when
 * the cadence can't produce one (one-off/unrecognised, or it hasn't started yet).
 */
const mostRecentExpectedRun = (start: Date, repeatsCode: string | null, now: Date): Date | null => {
    if (now < start) return null;
    switch (repeatsCode) {
        case Repeats.HOURLY:
            return addHours(start, differenceInHours(now, start));
        case Repeats.DAILY:
            return addDays(start, differenceInDays(now, start));
        case Repeats.WEEKLY:
            return addWeeks(start, differenceInWeeks(now, start));
        case Repeats.MONTHLY:
            return addMonths(start, differenceInCalendarMonths(now, start));
        default:
            return null;
    }
};

/**
 * `pending`/`queued` mean a worker hasn't actually picked the task up yet - a due task stuck in
 * either is itself the stall symptom (enqueued for the expected tick, never run), not evidence
 * against one, so it can't count as "a task ran for this tick" any more than no task at all.
 */
const isUnstarted = (task: ITaskRowViewModel): boolean =>
    task.statusCode === TaskStatus.PENDING || task.statusCode === TaskStatus.QUEUED;

/**
 * The most recent task that has actually happened, from a newest-first list. A pending/queued
 * task scheduled strictly in the future is a pre-enqueued next run, not evidence of anything yet,
 * so it's skipped in favour of the last task that actually started. But a task that already
 * reached a terminal outcome (success/failed) has unambiguously already run regardless of what its
 * nominal `scheduledDateTime` says relative to `now` (clock skew, timezone quirks) - only
 * pending/queued tasks get the future-scheduled exemption.
 */
export const findDueTask = (tasks: ITaskRowViewModel[], now: Date): ITaskRowViewModel | null =>
    tasks.find((task) => {
        const isPreEnqueuedForFuture =
            isUnstarted(task) && task.scheduledDateTime !== null && task.scheduledDateTime > now;
        return !isPreEnqueuedForFuture;
    }) ?? null;

const isStalled = (row: IScheduleRowViewModel, latestTask: ITaskRowViewModel, now: Date): boolean => {
    if (!row.startTime) return false;
    if (row.endTime && now > row.endTime) return false;

    const expectedRun = mostRecentExpectedRun(row.startTime, row.repeatsCode, now);
    if (!expectedRun) return false;
    if (row.endTime && expectedRun > row.endTime) return false;

    // Within grace of the current tick, a missing/unstarted task for *that* tick is expected -
    // it may simply not have landed yet. But grace only covers the current tick: if the tick
    // before it also has no task to show for it, the schedule is already at least one full cycle
    // behind, and that can't be excused away just because `now` happens to be close to "today's"
    // tick - otherwise a schedule dead for years would read as healthy ~25% of the time purely by
    // chance of when it's checked (grace-window minutes / cadence-length minutes).
    const withinGraceOfCurrentTick = now < addMinutes(expectedRun, STALLED_GRACE_MINUTES);
    const priorTick = withinGraceOfCurrentTick
        ? mostRecentExpectedRun(row.startTime, row.repeatsCode, addMinutes(expectedRun, -1))
        : null;
    if (
        withinGraceOfCurrentTick &&
        (!priorTick || (latestTask.scheduledDateTime && latestTask.scheduledDateTime >= priorTick))
    ) {
        return false;
    }

    if (isUnstarted(latestTask)) return true;
    return !latestTask.scheduledDateTime || latestTask.scheduledDateTime < expectedRun;
};

/**
 * `failed`/`stalled`/`unknown` only ever override a schedule the API reports as `active` - a
 * suspended or expired schedule producing no recent tasks is expected behaviour, not a health
 * signal, so those pass through unchanged. `failed` takes precedence over `stalled` since a
 * failing worker is a stronger signal than a missed tick.
 */
export const deriveScheduleStatus = (
    row: IScheduleRowViewModel,
    latestTask: ITaskRowViewModel | null,
    now: Date,
): string => {
    if (row.status !== ScheduleStatus.ACTIVE) return row.status;
    if (!latestTask) return DerivedScheduleStatus.UNKNOWN;
    if (latestTask.statusCode === TaskStatus.FAILED) return DerivedScheduleStatus.FAILED;
    if (isStalled(row, latestTask, now)) return DerivedScheduleStatus.STALLED;
    return row.status;
};

export default deriveScheduleStatus;
